import React, { useEffect, useState, useMemo } from 'react';
import {
  Users,
  UserPlus,
  Shield,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Eye,
  Trash2,
  Key,
  Layers,
  Mail,
  Megaphone,
  FileText,
  Gauge,
  CreditCard,
  Activity,
  Search,
  Loader2,
  RefreshCw,
  Lock,
  BarChart3,
} from 'lucide-react';
import { apiClient } from '../services/api-client';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import type { OrganizationMemberDto } from '@antigravity/types';

interface ScreenToggle {
  route: string;
  name: string;
  category: string;
  icon: React.ElementType;
  isAllowed: boolean;
  description: string;
}

const AVAILABLE_SCREENS: Omit<ScreenToggle, 'isAllowed'>[] = [
  { route: '/', name: 'Overview Dashboard', category: 'Core Platform', icon: Layers, description: 'Central operations topology and system telemetry' },
  { route: '/webmail', name: 'Webmail Client', category: 'Email Operations', icon: Mail, description: 'Direct Stalwart mailbox access, inbox reading, and compose' },
  { route: '/campaigns', name: 'Email Campaigns', category: 'Marketing Sequences', icon: Megaphone, description: 'Campaign authoring, sequence scheduling, and dispatch' },
  { route: '/analytics', name: 'Email Analytics & Reports', category: 'Marketing Sequences', icon: BarChart3, description: 'Sent volumes, open telemetry, bounce tracking, and recipient activity logs' },
  { route: '/templates', name: 'Email Templates', category: 'Marketing Sequences', icon: FileText, description: 'HTML template designer, pre-made cards, and variables' },
  { route: '/contacts', name: 'Audience & Contacts', category: 'Marketing Sequences', icon: Users, description: 'Audience lists, CSV bulk import, and status tracking' },
  { route: '/deliverability', name: 'Deliverability & Warmup', category: 'Reputation Engine', icon: Gauge, description: 'Sender score curves, automated warmup, and IP pools' },
  { route: '/team', name: 'Team & Employees', category: 'Organization', icon: Users, description: 'Employee provisioning, credentials, and screen permissions' },
  { route: '/billing', name: 'Billing & Quotas', category: 'Commerce', icon: CreditCard, description: 'Plan quotas, subscription management, and payment receipts' },
  { route: '/api-keys', name: 'API Keys', category: 'Developer Tools', icon: Key, description: 'Tenant-scoped HMAC bearer tokens for programmatic access' },
  { route: '/health', name: 'Cluster Diagnostics', category: 'Infrastructure', icon: Activity, description: 'Socket connectivity, Stalwart health, and Redis latency' },
];

export const TeamPage: React.FC = () => {
  const { currentOrg } = useAuth();
  const { refreshPermissions } = usePermissions();

  const [members, setMembers] = useState<OrganizationMemberDto[]>([]);
  const [customRoles, setCustomRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Create Employee Modal State
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'MEMBER',
  });
  const [screenToggles, setScreenToggles] = useState<Record<string, boolean>>({
    '/': true,
    '/webmail': true,
    '/campaigns': true,
    '/templates': true,
    '/contacts': true,
    '/deliverability': false,
    '/team': false,
    '/billing': false,
    '/api-keys': false,
    '/health': false,
  });

  // Edit Screen Permissions Modal State
  const [editingMember, setEditingMember] = useState<OrganizationMemberDto | null>(null);
  const [editToggles, setEditToggles] = useState<Record<string, boolean>>({});
  const [loadingPermissions, setLoadingPermissions] = useState<boolean>(false);
  const [savingPermissions, setSavingPermissions] = useState<boolean>(false);

  const fetchMembers = async () => {
    if (!currentOrg?.id) return;
    setLoading(true);
    try {
      const [membersData, rolesData] = await Promise.all([
        apiClient.listMembers(currentOrg.id),
        apiClient.getCustomRoles(currentOrg.id).catch(() => []),
      ]);
      setMembers(membersData || []);
      setCustomRoles(rolesData || []);
    } catch (err: any) {
      console.error('Failed to load team members:', err);
      setStatusMessage({ type: 'error', text: err.message || 'Failed to fetch team members' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [currentOrg?.id]);

  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      const name = `${m.user?.firstName || ''} ${m.user?.lastName || ''}`.toLowerCase();
      const email = (m.user?.email || '').toLowerCase();
      const q = searchQuery.toLowerCase();
      return name.includes(q) || email.includes(q) || m.role.toLowerCase().includes(q);
    });
  }, [members, searchQuery]);

  // Handle Preset selection for Create Modal
  const applyPreset = (preset: 'ALL' | 'MARKETING' | 'SUPPORT' | 'VIEWER') => {
    if (preset === 'ALL') {
      const all: Record<string, boolean> = {};
      AVAILABLE_SCREENS.forEach((s) => (all[s.route] = true));
      setScreenToggles(all);
    } else if (preset === 'MARKETING') {
      setScreenToggles({
        '/': true,
        '/webmail': true,
        '/campaigns': true,
        '/templates': true,
        '/contacts': true,
        '/deliverability': true,
        '/team': false,
        '/billing': false,
        '/api-keys': false,
        '/health': false,
      });
    } else if (preset === 'SUPPORT') {
      setScreenToggles({
        '/': true,
        '/webmail': true,
        '/campaigns': false,
        '/templates': false,
        '/contacts': true,
        '/deliverability': true,
        '/team': false,
        '/billing': false,
        '/api-keys': false,
        '/health': true,
      });
    } else if (preset === 'VIEWER') {
      setScreenToggles({
        '/': true,
        '/webmail': false,
        '/campaigns': true,
        '/templates': true,
        '/contacts': true,
        '/deliverability': false,
        '/team': false,
        '/billing': false,
        '/api-keys': false,
        '/health': false,
      });
    }
  };

  const handleGeneratePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let pass = '';
    for (let i = 0; i < 12; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData((prev) => ({ ...prev, password: pass }));
  };

  const handleCreateEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg?.id) return;
    setCreating(true);
    setStatusMessage(null);

    const screenPermissions = Object.entries(screenToggles).map(([route, isAllowed]) => ({
      route,
      isAllowed,
    }));

    try {
      await apiClient.createUamEmployee(currentOrg.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        screenPermissions,
      });

      setStatusMessage({
        type: 'success',
        text: `Employee ${formData.firstName} (${formData.email}) created successfully and mapped to this workspace!`,
      });
      setShowCreateModal(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'MEMBER',
      });
      await fetchMembers();
    } catch (err: any) {
      console.error('Failed to create employee:', err);
      setStatusMessage({ type: 'error', text: err.message || 'Failed to create employee' });
    } finally {
      setCreating(false);
    }
  };

  // Open Edit Screen Permissions Modal
  const handleOpenEditPermissions = async (member: OrganizationMemberDto) => {
    if (!currentOrg?.id) return;
    setEditingMember(member);
    setLoadingPermissions(true);
    try {
      const perms = await apiClient.getMemberScreenPermissions(currentOrg.id, member.id);
      const toggleMap: Record<string, boolean> = {};
      AVAILABLE_SCREENS.forEach((s) => {
        const found = perms.find((p) => p.route === s.route);
        toggleMap[s.route] = found ? Boolean(found.isAllowed) : false;
      });
      setEditToggles(toggleMap);
    } catch (err: any) {
      console.error('Failed to load member screen permissions:', err);
      setStatusMessage({ type: 'error', text: 'Failed to load screen permissions' });
    } finally {
      setLoadingPermissions(false);
    }
  };

  // Save Edit Screen Permissions
  const handleSavePermissions = async () => {
    if (!currentOrg?.id || !editingMember) return;
    setSavingPermissions(true);
    try {
      const permissions = Object.entries(editToggles).map(([route, isAllowed]) => ({
        route,
        isAllowed,
      }));
      await apiClient.updateMemberScreenPermissions(currentOrg.id, editingMember.id, permissions);
      setStatusMessage({
        type: 'success',
        text: `Screen permissions updated for ${editingMember.user?.firstName || 'Employee'}!`,
      });
      setEditingMember(null);
      await refreshPermissions();
    } catch (err: any) {
      console.error('Failed to save screen permissions:', err);
      setStatusMessage({ type: 'error', text: err.message || 'Failed to save screen permissions' });
    } finally {
      setSavingPermissions(false);
    }
  };

  // Delete Member Confirmation State
  const [memberToDelete, setMemberToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const handleRoleChange = async (memberId: string, newRole: string) => {
    if (!currentOrg?.id) return;
    try {
      await apiClient.updateMemberRole(currentOrg.id, memberId, { role: newRole as any });
      setStatusMessage({ type: 'success', text: `Role updated to ${newRole}` });
      await fetchMembers();
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message || 'Failed to update member role' });
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!currentOrg?.id || !memberToDelete) return;
    setDeleting(true);
    try {
      await apiClient.removeMember(currentOrg.id, memberToDelete.id);
      setStatusMessage({ type: 'success', text: `${memberToDelete.name} has been removed from the workspace` });
      setMemberToDelete(null);
      await fetchMembers();
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message || 'Failed to remove member' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2.5">
            <Users className="w-6 h-6 text-brand-400" />
            <span>Team & Employee Management</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Create employee accounts mapped to <span className="text-brand-300 font-semibold">{currentOrg?.name || 'Your Workspace'}</span> and configure precise screen visibility permissions.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchMembers}
            disabled={loading}
            className="p-2.5 rounded-xl border border-slate-700 bg-slate-900/80 hover:bg-slate-800 text-slate-300 transition"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2.5 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white shadow-lg shadow-brand-950/40 transition flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Create Employee
          </button>
        </div>
      </div>

      {/* Status Alert */}
      {statusMessage && (
        <div
          className={`p-3.5 rounded-xl text-xs flex items-center justify-between border ${
            statusMessage.type === 'success'
              ? 'bg-emerald-950/40 border-emerald-800/80 text-emerald-300'
              : 'bg-rose-950/40 border-rose-800/80 text-rose-300'
          }`}
        >
          <div className="flex items-center gap-2">
            {statusMessage.type === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-rose-400" />}
            <span>{statusMessage.text}</span>
          </div>
          <button onClick={() => setStatusMessage(null)} className="text-slate-400 hover:text-white">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400">Total Members</span>
            <Users className="w-4 h-4 text-brand-400" />
          </div>
          <div className="text-2xl font-bold text-white mt-2">{members.length}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Assigned to this organization</div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400">Workspace Owners</span>
            <Shield className="w-4 h-4 text-amber-400" />
          </div>
          <div className="text-2xl font-bold text-amber-400 mt-2">
            {members.filter((m) => m.role === 'OWNER').length}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">Full administrative authority</div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400">Employees & Members</span>
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="text-2xl font-bold text-emerald-400 mt-2">
            {members.filter((m) => m.role !== 'OWNER').length}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">Screen-restricted accounts</div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400">Registered Screens</span>
            <Layers className="w-4 h-4 text-purple-400" />
          </div>
          <div className="text-2xl font-bold text-purple-400 mt-2">{AVAILABLE_SCREENS.length}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Granularly configurable</div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by name, email, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900/60 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 placeholder-slate-500"
          />
        </div>
      </div>

      {/* Team Table */}
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="p-4 bg-slate-900/80 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <span>Workspace Employee Roster</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 font-mono">
              {filteredMembers.length}
            </span>
          </h2>
          <span className="text-xs text-slate-400">
            Click <strong className="text-brand-300">"Screen Permissions"</strong> to configure visible screens for any employee.
          </span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-brand-400" />
            <span className="text-xs">Loading workspace members...</span>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="p-12 text-center text-slate-400 space-y-2">
            <Users className="w-8 h-8 text-slate-600 mx-auto" />
            <p className="text-sm font-semibold text-white">No employees found</p>
            <p className="text-xs text-slate-500">Create your first employee account using the button above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                  <th className="py-3 px-4">Member Name</th>
                  <th className="py-3 px-4">Email Address</th>
                  <th className="py-3 px-4">Workspace Role</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-center">Screen Permissions</th>
                  <th className="py-3 px-4 text-right">Role Assignment</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {filteredMembers.map((member) => {
                  const fullName = `${member.user?.firstName || 'User'} ${member.user?.lastName || ''}`.trim();
                  const isOwner = member.role === 'OWNER';

                  return (
                    <tr key={member.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center font-bold text-xs text-brand-300">
                            {member.user?.firstName?.[0] || 'U'}
                            {member.user?.lastName?.[0] || ''}
                          </div>
                          <div>
                            <div className="font-semibold text-white">{fullName}</div>
                            {isOwner && <span className="text-[10px] text-amber-400 font-mono">Workspace Creator</span>}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 font-mono text-slate-300">{member.user?.email}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${
                            isOwner
                              ? 'bg-amber-950/80 text-amber-300 border-amber-800/80'
                              : member.role === 'ADMIN'
                              ? 'bg-blue-950/80 text-blue-300 border-blue-800/80'
                              : 'bg-emerald-950/80 text-emerald-300 border-emerald-800/80'
                          }`}
                        >
                          {member.role}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-950/80 text-emerald-400 border border-emerald-800/80">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                          ACTIVE
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {isOwner ? (
                          <span className="text-[11px] text-amber-400 font-medium">All Screens Granted (Owner)</span>
                        ) : (
                          <button
                            onClick={() => handleOpenEditPermissions(member)}
                            className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-slate-800 hover:bg-slate-700 border border-slate-700 text-brand-300 hover:text-white transition inline-flex items-center gap-1.5"
                          >
                            <Eye className="w-3.5 h-3.5 text-brand-400" />
                            Configure Screen Access
                          </button>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {isOwner ? (
                          <span className="text-slate-500 italic text-[11px]">Fixed (Owner)</span>
                        ) : (
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.id, e.target.value)}
                            className="bg-slate-900 border border-slate-700 text-slate-200 text-xs rounded-lg px-2.5 py-1 focus:outline-none focus:border-brand-500 font-medium cursor-pointer"
                          >
                            <option value="MEMBER">MEMBER</option>
                            {customRoles
                              .filter((cr) => !cr.isSystem && cr.code !== 'OWNER' && cr.code !== 'SUPER_ADMIN')
                              .map((cr) => (
                                <option key={cr.code} value={cr.code}>
                                  {cr.name} ({cr.code})
                                </option>
                              ))}
                            {member.role !== 'OWNER' && member.role !== 'MEMBER' && !customRoles.some((cr) => cr.code === member.role) && (
                              <option value={member.role}>{member.role}</option>
                            )}
                          </select>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {!isOwner ? (
                          <button
                            type="button"
                            onClick={() => setMemberToDelete({ id: member.id, name: fullName })}
                            className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-950/40 rounded-lg transition"
                            title="Remove Member"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <span className="text-[11px] text-slate-600 italic">Owner</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===================================================================== */}
      {/* MODAL 1: CREATE EMPLOYEE + SCREEN PERMISSIONS CONFIGURATOR */}
      {/* ===================================================================== */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden my-auto">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-800 shrink-0">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-brand-400" />
                  Create Employee & Map Screen Permissions
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  The user will be created under <span className="text-brand-300 font-semibold">{currentOrg?.name}</span> and will only see screens you grant below.
                </p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateEmployee} className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">First Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. John"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Last Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Doe"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Email Address (Login ID) *</label>
                  <input
                    type="email"
                    required
                    placeholder="employee@domain.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 font-mono"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold text-slate-300">Initial Password *</label>
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      className="text-[10px] text-brand-400 hover:text-brand-300 font-semibold"
                    >
                      Generate Strong
                    </button>
                  </div>
                  <input
                    type="text"
                    required
                    placeholder="Min 6 characters"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Workspace Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                >
                  <option value="MEMBER">MEMBER — Standard Employee</option>
                  {customRoles
                    .filter((cr) => !cr.isSystem && cr.code !== 'OWNER' && cr.code !== 'SUPER_ADMIN')
                    .map((cr) => (
                      <option key={cr.code} value={cr.code}>
                        {cr.code} — {cr.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Screen Permissions Matrix */}
              <div className="pt-2 border-t border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-bold text-white flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5 text-brand-400" />
                      Screen Visibility Controls (Show / Hide per Screen)
                    </h4>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Check the screens this employee can see. Unchecked screens are completely hidden from their sidebar and blocked.
                    </p>
                  </div>

                  {/* Preset Buttons */}
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => applyPreset('ALL')}
                      className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] text-slate-200 font-medium"
                    >
                      Show All
                    </button>
                    <button
                      type="button"
                      onClick={() => applyPreset('MARKETING')}
                      className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] text-brand-300 font-medium"
                    >
                      Marketing
                    </button>
                    <button
                      type="button"
                      onClick={() => applyPreset('VIEWER')}
                      className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] text-slate-300 font-medium"
                    >
                      Read-Only
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-1 bg-slate-950/60 rounded-xl border border-slate-800/80">
                  {AVAILABLE_SCREENS.map((screen) => {
                    const Icon = screen.icon;
                    const isChecked = Boolean(screenToggles[screen.route]);

                    return (
                      <div
                        key={screen.route}
                        onClick={() =>
                          setScreenToggles((prev) => ({
                            ...prev,
                            [screen.route]: !prev[screen.route],
                          }))
                        }
                        className={`p-2.5 rounded-xl border flex items-center justify-between cursor-pointer transition select-none ${
                          isChecked
                            ? 'bg-brand-950/30 border-brand-800/60 text-white'
                            : 'bg-slate-900/40 border-slate-800/60 text-slate-500 hover:bg-slate-900'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon className={`w-4 h-4 ${isChecked ? 'text-brand-400' : 'text-slate-600'}`} />
                          <div>
                            <div className="text-xs font-semibold">{screen.name}</div>
                            <div className="text-[10px] font-mono text-slate-500">{screen.route}</div>
                          </div>
                        </div>

                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            isChecked ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-slate-800 text-slate-400'
                          }`}
                        >
                          {isChecked ? 'SHOW' : 'HIDE'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-slate-800 bg-slate-900 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2 shadow-lg shadow-brand-950/40"
                >
                  {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Create & Map Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* MODAL 2: EDIT SCREEN PERMISSIONS FOR EXISTING MEMBER */}
      {/* ===================================================================== */}
      {editingMember && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden my-auto">
            <div className="flex items-center justify-between p-4 sm:p-6 pb-3 border-b border-slate-800 shrink-0">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-brand-400" />
                  Screen Permissions: {editingMember.user?.firstName} {editingMember.user?.lastName}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Configure which screens this employee can view. Screens marked HIDE are completely blocked from their session.
                </p>
              </div>
              <button onClick={() => setEditingMember(null)} className="text-slate-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {loadingPermissions ? (
              <div className="p-12 text-center text-slate-400 flex flex-col items-center justify-center gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-brand-400" />
                <span className="text-xs">Loading employee screen permissions...</span>
              </div>
            ) : (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-1 bg-slate-950/60 rounded-xl border border-slate-800/80">
                    {AVAILABLE_SCREENS.map((screen) => {
                      const Icon = screen.icon;
                      const isChecked = Boolean(editToggles[screen.route]);

                      return (
                        <div
                          key={screen.route}
                          onClick={() =>
                            setEditToggles((prev) => ({
                              ...prev,
                              [screen.route]: !prev[screen.route],
                            }))
                          }
                          className={`p-2.5 rounded-xl border flex items-center justify-between cursor-pointer transition select-none ${
                            isChecked
                              ? 'bg-brand-950/30 border-brand-800/60 text-white'
                              : 'bg-slate-900/40 border-slate-800/60 text-slate-500 hover:bg-slate-900'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <Icon className={`w-4 h-4 ${isChecked ? 'text-brand-400' : 'text-slate-600'}`} />
                            <div>
                              <div className="text-xs font-semibold">{screen.name}</div>
                              <div className="text-[10px] font-mono text-slate-500">{screen.route}</div>
                            </div>
                          </div>

                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              isChecked ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-slate-800 text-slate-400'
                            }`}
                          >
                            {isChecked ? 'SHOW' : 'HIDE'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-slate-800 bg-slate-900 shrink-0">
                  <button
                    type="button"
                    onClick={() => setEditingMember(null)}
                    className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSavePermissions}
                    disabled={savingPermissions}
                    className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2 shadow-lg shadow-brand-950/40"
                  >
                    {savingPermissions && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Save Screen Permissions
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* MODAL 3: CONFIRM DELETE MEMBER */}
      {/* ===================================================================== */}
      {memberToDelete && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center gap-3 text-rose-400">
              <div className="p-2.5 rounded-xl bg-rose-950/60 border border-rose-800/60">
                <Trash2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Remove Employee</h3>
                <p className="text-xs text-slate-400">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              Are you sure you want to remove <strong className="text-white font-semibold">{memberToDelete.name}</strong> from this workspace? They will immediately lose access to all granted screens and sessions.
            </p>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setMemberToDelete(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRemoveMember}
                disabled={deleting}
                className="px-5 py-2 rounded-xl text-xs font-semibold bg-rose-600 hover:bg-rose-500 text-white flex items-center gap-2 shadow-lg shadow-rose-950/40 transition"
              >
                {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Confirm Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
